/**
 * @file Consultation speaker naming — the label→person map as it crosses the wire, and the one
 *       precedence chain that turns a transcript segment into the name a practitioner reads.
 * @module utils/consult/speakerNames
 *
 * S25 of ClickUp 86d3zmpwv. Sibling of `participantIdentity` (S8) and `audioSettings` (S23).
 *
 * ═══ THE MAP CROSSES THE WIRE IN TWO SHAPES ═══════════════════════════════════════════════
 * `roomInfo.speakerMap` is a JSON **string** (its sibling fields are stored strings passed
 * straight through), while `speakerMapUpdated.speakerMap` is the parsed **object**. `parseSpeakerMap`
 * takes either, so a page never has to know which one it is holding. Neither ever throws: a
 * malformed map must cost the practitioner their speaker names, not the whole consult page.
 *
 * ═══ WHY THE ROSTER IS NOT CONSULTED FOR A DIARISATION LABEL ══════════════════════════════
 * In person there is ONE microphone and several people in front of it, so every segment carries
 * `participantId: "practitioner"` no matter who spoke — the roster name for that id is the mic's
 * owner, not the speaker. `speakerLabel` is transcribe's answer to *who*, and when it says
 * something different from the participant id it is the only thing in the payload that does.
 * Reading the roster there would confidently label the patient's words with the practitioner's
 * name, which is worse than "Speaker 2".
 *
 * The roster therefore applies only when the effective label IS the participant id — online (where
 * `speakerLabel === participantId` by contract) and on an older transcribe that sends no label at
 * all. That is today's in-person behaviour, unchanged, until diarisation labels arrive.
 *
 * ═══ NEVER THE RAW ID ═════════════════════════════════════════════════════════════════════
 * The last resort is a prettified label, never the id itself: `patient_{uuid}` is how a raw UUID
 * used to reach the screen (and the avatar initial), and a uuid is not a name.
 */

import { ParticipantDisplayNames, SpeakerAssignment, SpeakerMap, SpeakerParticipantInput, SpeakerRole, Transcript } from "../../types/appointments/ConsultTypes";
import { TeamMember } from "../../types/admin/TeamTypes";
import { Patient } from "../../types/healthcare/PatientTypes";
import { IN_PERSON_PRACTITIONER_ID, MOBILE_MIC_DISPLAY_NAME, MOBILE_MIC_PARTICIPANT_ID } from "./participantIdentity";

export const SPEAKER_ROLES: SpeakerRole[] = ["practitioner", "patient", "other"];

/** talk rejects anything longer (`SPEAKER_DISPLAY_NAME_MAX_LENGTH`); the input stops it here. */
export const SPEAKER_DISPLAY_NAME_MAX_LENGTH = 120;

/** What an unrecognisable id resolves to. Honest, and not a fragment of a uuid. */
export const UNKNOWN_SPEAKER_NAME = "Unknown speaker";

/**
 * A map whose keys are client-supplied is built with a NULL PROTOTYPE, for the same reason talk
 * builds its copy that way: on a plain object `map["__proto__"] = entry` sets the prototype instead
 * of a property, so the entry vanishes from the very `JSON.stringify` that would persist it. Reads
 * still go through `hasOwnProperty` — a caller can hand us a plain object.
 */
export const emptySpeakerMap = (): SpeakerMap => Object.create(null) as SpeakerMap;

const isSpeakerRole = (value: unknown): value is SpeakerRole => typeof value === "string" && (SPEAKER_ROLES as string[]).includes(value);

/** One wire entry → an assignment, or `null` if it is not one. */
const toSpeakerAssignment = (value: unknown): SpeakerAssignment | null => {
	if (!value || typeof value !== "object") return null;

	const raw = value as Record<string, unknown>;
	const displayName = typeof raw.displayName === "string" ? raw.displayName.trim() : "";
	if (!displayName || !isSpeakerRole(raw.role)) return null;

	return {
		participantId: typeof raw.participantId === "string" ? raw.participantId : null,
		displayName,
		role: raw.role,
		source: raw.source === "enrollment" ? "enrollment" : "manual",
		updatedAt: typeof raw.updatedAt === "number" ? raw.updatedAt : undefined,
	};
};

/**
 * A `roomInfo.speakerMap` string or a `speakerMapUpdated.speakerMap` object → a usable map.
 *
 * Entries that do not validate are dropped individually: a single bad entry must not cost the
 * practitioner the names they set for every other speaker in the room.
 */
export const parseSpeakerMap = (value: unknown): SpeakerMap => {
	let candidate: unknown = value;

	// At most two unwraps — a JSON string, and a JSON string that decoded to another one. Same
	// tolerance `safeParseTranscripts` gives the transcript column, for the same storage reason.
	for (let depth = 0; depth < 2 && typeof candidate === "string"; depth += 1) {
		const trimmed = candidate.trim();
		if (!trimmed) return emptySpeakerMap();

		try {
			candidate = JSON.parse(trimmed);
		} catch {
			return emptySpeakerMap();
		}
	}

	if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) return emptySpeakerMap();

	const parsed = emptySpeakerMap();
	Object.keys(candidate as Record<string, unknown>).forEach((label) => {
		const assignment = toSpeakerAssignment((candidate as Record<string, unknown>)[label]);
		if (label && assignment) parsed[label] = assignment;
	});

	return parsed;
};

/** The entry for a label, or `null`. `hasOwnProperty` because the label arrives off the socket. */
export const speakerAssignmentFor = (label: string, speakerMap?: SpeakerMap | null): SpeakerAssignment | null => {
	if (!speakerMap || !label || !Object.prototype.hasOwnProperty.call(speakerMap, label)) return null;

	const assignment = speakerMap[label];
	return assignment && typeof assignment.displayName === "string" ? assignment : null;
};

/**
 * The map as it will look once the server has accepted an assignment — the optimistic update the
 * broadcast then replaces wholesale. A `participant` of `null` clears the label, matching
 * `assignSpeaker`.
 */
export const applySpeakerAssignment = (speakerMap: SpeakerMap, label: string, participant: SpeakerParticipantInput | null): SpeakerMap => {
	const next = emptySpeakerMap();
	Object.keys(speakerMap || {}).forEach((key) => {
		next[key] = speakerMap[key];
	});

	if (!label) return next;

	if (!participant) {
		delete next[label];
		return next;
	}

	next[label] = {
		participantId: participant.participantId ?? null,
		displayName: participant.displayName,
		role: participant.role,
		source: "manual",
	};

	return next;
};

/**
 * The label a segment is named by: transcribe's `speakerLabel`, falling back to the participant id.
 *
 * One code path serves both consult kinds — online, `speakerLabel === participantId` by contract,
 * and a transcribe old enough to send no label lands on the same value.
 */
export const effectiveSpeakerLabel = (segment: Pick<Transcript, "participantId" | "speakerLabel">): string =>
	(typeof segment.speakerLabel === "string" && segment.speakerLabel.trim()) || segment.participantId || "";

/** `speaker_2` → `Speaker 2`. transcribe mints these 1-based (`improved_speaker_matching.py`). */
const DIARISATION_LABEL = /^speaker[_-]?(\d+)$/i;

/**
 * A label nobody has named → something readable.
 *
 * The id formats are the ones `participantIdentity` documents: the in-person practitioner's literal
 * `practitioner`, the paired phone's `mobile-mic`, video's `practitioner_{userId}` and the
 * patient's `patient_{uuid}`. Anything else is not shown at all — an unrecognised id is far more
 * likely to be a uuid than a name.
 */
export const prettifySpeakerLabel = (label: string): string => {
	const trimmed = (label || "").trim();
	if (!trimmed) return UNKNOWN_SPEAKER_NAME;

	const diarised = DIARISATION_LABEL.exec(trimmed);
	if (diarised) return `Speaker ${Number(diarised[1])}`;

	if (trimmed === MOBILE_MIC_PARTICIPANT_ID) return MOBILE_MIC_DISPLAY_NAME;

	const prefix = trimmed.split("_")[0].toLowerCase();
	if (prefix === IN_PERSON_PRACTITIONER_ID) return "Practitioner";
	if (prefix === "patient") return "Patient";

	return UNKNOWN_SPEAKER_NAME;
};

/**
 * `hasOwnProperty`, never a bare `displayNames[id]`: the id comes off the socket, and a bare read
 * on an object literal returns an INHERITED member for `toString`/`constructor` — a function where
 * a string is typed.
 */
const rosterName = (displayNames: ParticipantDisplayNames | undefined, participantId: string): string => {
	if (!displayNames || !participantId || !Object.prototype.hasOwnProperty.call(displayNames, participantId)) return "";

	const name = displayNames[participantId];
	return typeof name === "string" ? name.trim() : "";
};

/**
 * The name shown against a segment: an assigned name, else the roster's, else a prettified label.
 *
 * See this file's header for why the roster is skipped when the segment carries a diarisation
 * label that differs from its participant id.
 */
export const resolveSpeakerName = (
	segment: Pick<Transcript, "participantId" | "speakerLabel">,
	speakerMap?: SpeakerMap | null,
	displayNames?: ParticipantDisplayNames,
): string => {
	const label = effectiveSpeakerLabel(segment);

	const assigned = speakerAssignmentFor(label, speakerMap);
	if (assigned?.displayName.trim()) return assigned.displayName.trim();

	if (label === segment.participantId) {
		const roster = rosterName(displayNames, segment.participantId);
		if (roster) return roster;
	}

	return prettifySpeakerLabel(label);
};

/**
 * Which side of the thread a segment belongs on: an assigned role, else a guess from the
 * effective label's own prefix. `null` is deliberate — guessing a side is worse than a neutral one.
 */
export const speakerRoleFor = (segment: Pick<Transcript, "participantId" | "speakerLabel">, speakerMap?: SpeakerMap | null): SpeakerRole | null => {
	const label = effectiveSpeakerLabel(segment);

	const assigned = speakerAssignmentFor(label, speakerMap);
	if (assigned) return assigned.role;

	if (label === MOBILE_MIC_PARTICIPANT_ID) return "other";

	const prefix = label.split("_")[0].toLowerCase();
	if (prefix === IN_PERSON_PRACTITIONER_ID) return "practitioner";
	if (prefix === "patient") return "patient";

	return null;
};

const fullName = (person?: Pick<TeamMember | Patient, "first_name" | "last_name"> | null): string => `${person?.first_name || ""} ${person?.last_name || ""}`.trim();

/**
 * The real-name `displayNames` map for one appointment record — built from who was actually in
 * the transcript, so a rejected/unmatched id is simply absent rather than guessed at.
 */
export const rosterForRecord = (
	transcripts: Transcript[],
	record: { practitioner?: TeamMember | null; teamMembers?: TeamMember[]; patient?: Patient | null },
): ParticipantDisplayNames => {
	const roster = Object.create(null) as ParticipantDisplayNames;
	const practitionerName = fullName(record.practitioner);
	const patientName = fullName(record.patient);

	const participantIds = new Set<string>();
	transcripts.forEach((t) => {
		if (t.participantId) participantIds.add(t.participantId);
	});

	participantIds.forEach((id) => {
		if (id === MOBILE_MIC_PARTICIPANT_ID) return; // prettifySpeakerLabel already says "Room microphone"

		if (id === IN_PERSON_PRACTITIONER_ID) {
			if (practitionerName) roster[id] = practitionerName;
			return;
		}

		if (id.startsWith("patient_")) {
			if (patientName) roster[id] = patientName;
			return;
		}

		const match = /^practitioner_(.+)$/.exec(id);
		if (!match) return;

		// Transcripts recorded before the uuid cutover carry the legacy integer id.
		const isSpeaker = (m?: Pick<TeamMember, "id" | "legacy_user_id"> | null): boolean =>
			!!m && (m.id === match[1] || (m.legacy_user_id != null && String(m.legacy_user_id) === match[1]));
		const name = isSpeaker(record.practitioner) ? practitionerName : fullName((record.teamMembers || []).find(isSpeaker));
		if (name) roster[id] = name;
	});

	return roster;
};
